import { Ajax } from "./modules/ajax/ajax.js";
import { Dialog } from "./modules/dialog/dialog.js";
import { Toast } from "./modules/dialog/toast.js";
import { Razor } from "./modules/razor/razor.js";
import { ListView } from "./modules/components/list-view.js";
import { FileUploader } from "./modules/io/fileuploader.js";
import { BlurredMenu } from "./modules/components/blurred-menu.js";
import { Browser } from "./modules/core/browser.js";
import { DbInterfacer } from "./modules/database/db-interfacer.js";
import { Card } from "./modules/components/card/card.js";
import { Checkbox } from "./modules/components/checkbox/checkbox.js";
import { Textarea } from "./modules/components/textarea/textarea.js";
import { InfoGroup } from "./modules/components/infogroup/info-group.js";
import { InfoGroupRow } from "./modules/components/infogroup/infogroup-row.js";
import { InfoGroupValue } from "./modules/components/infogroup/infogroup-value.js";
import { Input } from "./modules/components/input/input.js";
import { Dropdown } from "./modules/components/dropdown/dropdown.js";
import { DropdownItem } from "./modules/components/dropdown/dropdown-item.js";
import { Callout } from "./modules/components/callout/callout.js";
import { SortableList } from "./modules/components/sortablelist/sortable-list.js";
import { SortableListItem } from "./modules/components/sortablelist/sortable-listitem.js";
import { Tree } from "./modules/components/tree/tree.js";
import { TreeItem } from "./modules/components/tree/tree-item.js";
import { DropdownDivider } from "./modules/components/dropdown/dropdown-divider.js";
import { DropdownGroup } from "./modules/components/dropdown/dropdown-group.js";
import { DropdownHeader } from "./modules/components/dropdown/dropdown-header.js";
import { MenuItem } from "./modules/components/contextmenu/menu-item.js";
import { ContextMenu } from "./modules/components/contextmenu/context-menu.js";
import { MenuDivider } from "./modules/components/contextmenu/menu-divider.js";
import { Accordion } from "./modules/components/accordion/accordion.js";
import { AccordionItem } from "./modules/components/accordion/accordion-item.js";
import { TabGroup } from "./modules/components/tabs/tabgroup.js";
import { Tab } from "./modules/components/tabs/tab.js";
import { Form } from "./modules/components/form/form.js";
import { FormSection } from "./modules/components/form/form-section.js";
import { Prompt } from "./modules/components/emptyprompt/prompt.js";
import { Button } from "./modules/components/button/button.js";
import { SplitButton } from "./modules/components/button/splitbutton.js";
import { DropdownButton } from "./modules/components/button/dropdown-button.js";
import { IconPicker } from "./modules/components/iconpicker/iconpicker.js";
import { ButtonGroup } from "./modules/components/button/button-group.js";
import { ColourPicker } from "./modules/components/colourpicker/colourpicker.js";
import { Badge } from "./modules/components/badge/badge.js";
import { Label } from "./modules/components/label/label.js";
import { Header } from "./modules/components/header/header.js";
import { Range } from "./modules/components/range/range.js";
import { Breadcrumb } from "./modules/components/breadcrumb/breadcrumb.js";
import { BreadcrumbItem } from "./modules/components/breadcrumb/breadcrumb-item.js";
import { Progress } from "./modules/components/progress/progress.js";
import { ProgressItem } from "./modules/components/progress/progress-item.js";
import { Radio } from "./modules/components/radio/radio.js";
import { CodeBlock } from "./modules/components/code-block/code-block.js";
import { hljs as CodeHighlight } from "./highlight.min.js";

const localizationHandler = {
	get(target, key) {
		return key in target ? target[key] : key;
	},
	set(target, key, value) {
		const func = Reflect.set(target, key, value);

		Object.assign(Dialog.DefaultOptions, {
			ButtonText: Localizer.DIALOG_BUTTON_CLOSE,
			AffirmativeText: Localizer.DIALOG_BUTTON_AFFIRMATIVE,
			NegativeText: Localizer.DIALOG_BUTTON_NEGATIVE
		});

		return func;
	}
};

Object.assign(global, {
	Localizer: new Proxy({}, localizationHandler),
	Ajax, Dialog, Toast, ContextMenu, ListView,
	FileUploader, BlurredMenu, Razor, Browser, DbInterfacer, CodeHighlight
});

Document.prototype.createElementWithContents = function (nodeName, content, attributes) {
	const element = Object.assign(this.createElement(nodeName), attributes);

	if (content instanceof NodeList || content instanceof HTMLCollection || Array.isArray(content)) { element.append(...content); }
	else if (content instanceof Node) { element.appendChild(content); }
	else if (content != null) { element.innerHTML = content.toString(); }

	return element;
}

HTMLElement.prototype.toggleClass = function (className, add) {
	if (add == null) {
		add = !this.classList.contains(className);
	}

	if (add) {
		this.classList.add(className);
	} else {
		this.removeClass(className);
	}
}

HTMLElement.prototype.enhance = function () {
	[...this.childNodes ?? []].filter(x => x.nodeType === Node.TEXT_NODE).forEach(node => {
		const replacement = document.createElementWithContents("span", node.textContent)
		node.replaceWith(replacement);

		replacement.innerHTML = replacement.innerHTML
			.replace(/(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/ig, url => `<a target='_blank' href='${url}'>${url}</a>`)
			.replace(/[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/ig, email => `<a href='mailto:${email}'>${email}</a>`);
	});
}

function trimBreaksFromElement(element) {
	let lines = element.textContent.split('\n');

	if (lines.at(0) === "") { lines.shift(); }

	let matches,
		indentation = (matches = /^\s+/.exec(lines[0])) !== null ? matches[0] : null;

	if (indentation) {
		lines = lines.map(line => line.replace(indentation, '').replace(/\t/g, '    '));
		element.textContent = lines.join('\n').trim();
	}
}

HTMLElement.prototype.trimBreaks = function () {
	trimBreaksFromElement(this);
}

Node.prototype.trimBreaks = function () {
	trimBreaksFromElement(this);
}

HTMLElement.prototype.removeClasses = function (...classNames) {
	this.classList.remove(classNames);

	if (this.className === "") {
		this.removeAttribute("class");
	}
}

HTMLElement.prototype.removeClass = function (className) {
	this.removeClasses(className);
}

function similarity(s1, s2) {
	let longer = s1;
	let shorter = s2;

	if (s1.length < s2.length) {
		longer = s2;
		shorter = s1;
	}

	const longerLength = longer.length;

	if (longerLength == 0) {
		return 1.0;
	}
	return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
}

function editDistance(s1, s2) {
	s1 = s1.toLowerCase();
	s2 = s2.toLowerCase();

	const costs = [];

	for (let i = 0; i <= s1.length; i++) {
		let lastValue = i;

		for (let j = 0; j <= s2.length; j++) {
			if (i == 0) {
				costs[j] = j;
			}
			else if (j > 0) {
				let newValue = costs[j - 1];

				if (s1.charAt(i - 1) != s2.charAt(j - 1)) {
					newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
				}

				costs[j - 1] = lastValue;
				lastValue = newValue;
			}
		}

		if (i > 0) {
			costs[s2.length] = lastValue;
		}
	}

	return costs[s2.length];
}

// Functions

const isScrollable = node => {
	if (!(node instanceof HTMLElement || node instanceof SVGElement)) {
		return false
	}
	const style = getComputedStyle(node)
	return ['overflow', 'overflow-x', 'overflow-y'].some((propertyName) => {
		const value = style.getPropertyValue(propertyName)
		return value === 'auto' || value === 'scroll'
	})
}

global.getScrollParent = node => {
	let currentParent = node.parentElement
	while (currentParent) {
		if (isScrollable(currentParent)) {
			return currentParent
		}
		currentParent = currentParent.parentElement
	}
	return document.scrollingElement || document.documentElement
}

global.getClipboardText = async function () {
	try {
		const state = (await navigator.permissions.query({ name: 'clipboard-read' })).state;

		return state !== "denied"
			? await navigator.clipboard.readText()
			: "";
	} catch {
		// Firefox doesn't support reading from the clipboard
		return "";
	}
}

global.getClipboardData = async function () {
	try {
		const state = (await navigator.permissions.query({ name: 'clipboard-read' })).state;

		return state !== "denied"
			? await navigator.clipboard.read()
			: "";
	} catch {
		// Firefox being Firefox
		return null;
	}
}

global.writeClipboardText = async function (text) {
	try {
		const state = (await navigator.permissions.query({ name: 'clipboard-write' })).state;

		if (state !== "denied") {
			await navigator.clipboard.writeText(text);
		}
	} catch {
		await navigator.clipboard.writeText(text);
	}
}

global.writeClipboard = async function (items) {
	try {
		const state = (await navigator.permissions.query({ name: 'clipboard-write' })).state;

		if (state !== "denied") {
			await navigator.clipboard.write(items);
		}
	} catch {
		await navigator.clipboard.write(items);
	}
}

global.toTitleCase = function (str) {
	let upper = true;
	let newStr = "";

	for (let i = 0, l = str.length; i < l; i++) {
		if (str[i] == " ") {
			upper = true;
			newStr += str[i];

			continue;
		}

		newStr += upper ? str[i].toUpperCase() : str[i].toLowerCase();
		upper = false;
	}

	return newStr;
}

global.SortCaseInsensitive = function (a, b) {
	return a.toLowerCase().localeCompare(b.toLowerCase());
}

global.GroupBy = (array, fn) => array.reduce((a, b, i) => {
	const key = fn(b, i, array);

	a[key] ||= [];
	a[key].push(b);

	return a;
}, {});

global.SearchArray = function (array, query, propertyName, minLength) {
	const splitQuery = query.toUpperCase().split(" "),
		matchWholeProp = query.length < minLength;

	let arrSearched = array.
		map(x => {
			const comparator = propertyName ? x[propertyName] : x;

			const obj = {
				IsMatch: false,
				OriginalElement: x,
				MatchLevel: similarity(comparator.toUpperCase(), query)
			}

			if (matchWholeProp) {
				obj.IsMatch = comparator.toUpperCase() === query.toUpperCase();
			} else {
				obj.IsMatch = splitQuery.find(y => comparator.toUpperCase().includes(y));
			}

			return obj;
		}).filter(x => x.IsMatch).sort((a, b) => b.MatchLevel - a.MatchLevel);

	// If we're doing a fuzzy search, and there are results that contain ALL queries, reduce the fuzzy search to these results
	if (!matchWholeProp && splitQuery.length > 1) {
		const verboseMatches = arrSearched.filter(x => {
			const originalComparator = propertyName ? x.OriginalElement[propertyName] : x.OriginalElement;

			return !splitQuery.find(y => !originalComparator.toUpperCase().includes(y))
		});

		if (verboseMatches.length) {
			arrSearched = verboseMatches;
		}
	}

	return arrSearched.map(x => x.OriginalElement);
}

// Set default localizer strings
Localizer.DIALOG_BUTTON_CLOSE = "Close";
Localizer.DIALOG_BUTTON_AFFIRMATIVE = "Yes";
Localizer.DIALOG_BUTTON_NEGATIVE = "No";

Localizer.THEME_BUTTON_DARK = "Change to light mode";
Localizer.THEME_BUTTON_LIGHT = "Change to dark mode";
Localizer.TOGGLE_DROPDOWN = "Toggle dropdown";

Localizer.CONFIRMATION_TITLE = "Are you sure?";
Localizer.ERROR_TITLE = "Oh no!";
Localizer.WARNING_TITLE = "Warning";
Localizer.SUCCESS_TITLE = "Success!";

Localizer.DROPDOWN_REQUIRED = "Please select an option";

window.customElements.define("chip-card", Card);
window.customElements.define("chip-checkbox", Checkbox);
window.customElements.define("chip-input", Input);
window.customElements.define("chip-textarea", Textarea);
window.customElements.define("chip-infogroup", InfoGroup);
window.customElements.define("chip-inforow", InfoGroupRow);
window.customElements.define("chip-infovalue", InfoGroupValue);
window.customElements.define("chip-dropdown", Dropdown);
window.customElements.define("chip-dropdownitem", DropdownItem);
window.customElements.define("chip-dropdowndivider", DropdownDivider);
window.customElements.define("chip-dropdowngroup", DropdownGroup);
window.customElements.define("chip-dropdownheader", DropdownHeader);
window.customElements.define("chip-callout", Callout);
window.customElements.define("chip-sortablelist", SortableList);
window.customElements.define("chip-sortablelistitem", SortableListItem);
window.customElements.define("chip-tree", Tree);
window.customElements.define("chip-treeitem", TreeItem);
window.customElements.define("chip-contextmenu", ContextMenu);
window.customElements.define("chip-menuitem", MenuItem);
window.customElements.define("chip-menudivider", MenuDivider);
window.customElements.define("chip-accordion", Accordion);
window.customElements.define("chip-accordionitem", AccordionItem);
window.customElements.define("chip-button", Button);
window.customElements.define("chip-splitbutton", SplitButton);
window.customElements.define("chip-dropdownbutton", DropdownButton);
window.customElements.define("chip-tabgroup", TabGroup);
window.customElements.define("chip-tab", Tab);
window.customElements.define("chip-form", Form);
window.customElements.define("chip-formsection", FormSection);
window.customElements.define("chip-emptyprompt", Prompt);
window.customElements.define("chip-iconpicker", IconPicker);
window.customElements.define("chip-buttongroup", ButtonGroup);
window.customElements.define("chip-colourpicker", ColourPicker);
window.customElements.define("chip-badge", Badge);
window.customElements.define("chip-label", Label);
window.customElements.define("chip-header", Header);
window.customElements.define("chip-range", Range);
window.customElements.define("chip-breadcrumb", Breadcrumb);
window.customElements.define("chip-breadcrumbitem", BreadcrumbItem);
window.customElements.define("chip-progress", Progress);
window.customElements.define("chip-progressitem", ProgressItem);
window.customElements.define("chip-radio", Radio);
window.customElements.define("chip-codeblock", CodeBlock);

Razor.UpdateState();

function closeDropdownsFromEvent(ev) {
	let closestInput = ev.target?.closest?.("chip-dropdown")?.parentElement;
	if (closestInput?.nodeName !== "CHIP-INPUT") { closestInput = null; }

	[...document.querySelectorAll("chip-dropdown")].filter(x => (ev.target instanceof Node && !x.contains(ev.target)) && x !== ev.target?.closest?.("chip-dropdown") && !closestInput?.contains(x))
		.forEach(x => x.Close());
}

window.addEventListener('focus', ev => closeDropdownsFromEvent(ev), false);

document.body.addEventListener("mousedown", ev => {
	document.querySelectorAll("chip-contextmenu").forEach(x => x.HandleMenuBlurEvent(ev));

	closeDropdownsFromEvent(ev);
});